import {
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  DocumentSourceProviderConfigResponse,
  DocumentSourceUserConnectionRequest,
  DocumentSourceUserConnectionResponse,
} from '../../api/ApiClient';
import { apiClient } from '../../config/apiConfig';
import { JsonValue, ExtendedConnectionResponse } from '../../types/common';
import { log } from '../../utils/errors/logger';
import { useSnackbar } from '@/contexts/SnackbarContext';
import { useDialogState } from '@/hooks/ui/useDialogState';
import { useConnectionFormState } from '@/hooks/business/useConnectionFormState';
import { useConnectionOperations } from '@/hooks/business/useConnectionOperations';
import { ConnectionGrid } from './Sources/ConnectionGrid';
import { ConnectionFormDialog } from './Sources/ConnectionFormDialog';
import { DeleteConfirmationDialog } from '../ui/DeleteConfirmationDialog';
import { AddConnectionFab } from './Sources/AddConnectionFab';

const DocumentSources: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [availableProviders, setAvailableProviders] = useState<DocumentSourceProviderConfigResponse[]>([]);
  const [userConnections, setUserConnections] = useState<ExtendedConnectionResponse[]>([]);
  // Use custom hooks for dialog, snackbar, and form
  const dialogState = useDialogState<ExtendedConnectionResponse>();
  const { showSuccess, showError } = useSnackbar();
  const { formData, resetForm, populateForm, updateFormData } = useConnectionFormState();

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [providersResponse, connectionsResponse] = await Promise.all([
        apiClient.getAvailableProviders(),
        apiClient.getUserConnections(),
      ]);
      setAvailableProviders(providersResponse.data || []);
      setUserConnections(connectionsResponse.data as ExtendedConnectionResponse[] || []);
    } catch (error) {
      log.error('Failed to load document sources', { error: error as JsonValue });
      showError(t('documentSources.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t, showError]);

  // Operations hook that depends on loadData
  const operations = useConnectionOperations({
    showSuccess,
    showError,
    onDataChanged: loadData,
  });

  useEffect(() => {
    void loadData(); // Explicitly mark as intentionally not awaited
  }, [loadData]);

  const handleAddConnection = () => {
    dialogState.closeDialog();
    resetForm();
    dialogState.openDialog();
  };

  const handleEditConnection = (connection: ExtendedConnectionResponse) => {
    populateForm(connection);
    dialogState.openEditDialog(connection);
  };

  const handleDeleteConnection = async () => {
    if (!dialogState.itemToDelete) {
      return;
    }

    await operations.deleteConnection(dialogState.itemToDelete.id);
    dialogState.closeDeleteConfirm();
  };

  const handleSaveConnection = async (data: DocumentSourceUserConnectionRequest) => {
    await operations.saveConnection(data, dialogState.editingItem?.id);
    dialogState.closeDialog();
  };

  const handleTestConnection = (id: string) => {
    void operations.testConnection(id);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('documentSources.title')}
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        {t('documentSources.description')}
      </Typography>

      <ConnectionGrid
        connections={userConnections}
        onEdit={handleEditConnection}
        onDelete={dialogState.openDeleteConfirm}
        onTest={handleTestConnection}
      />

      <ConnectionFormDialog
        open={dialogState.dialogOpen}
        onClose={dialogState.closeDialog}
        onSave={handleSaveConnection}
        availableProviders={availableProviders}
        editingConnection={dialogState.editingItem as DocumentSourceUserConnectionResponse | null}
        formData={formData}
        onFormChange={updateFormData}
      />

      <DeleteConfirmationDialog
        open={dialogState.deleteConfirmOpen}
        onClose={dialogState.closeDeleteConfirm}
        onConfirm={handleDeleteConnection}
        title={t('documentSources.deleteDialog.title')}
        itemName={dialogState.itemToDelete?.name}
        message={t('documentSources.deleteDialog.message', { name: dialogState.itemToDelete?.name })}
      />

      <AddConnectionFab onClick={handleAddConnection} />
    </Box>
  );
};

export default DocumentSources;
